import { useMemo } from "react";
import { useGetAllTagTypes } from "src/api/client-api/tag/useGetAllTagTypes";
import { useGetAllTagValues } from "src/api/client-api/tag/useGetAllTagValues";
import { TagType } from "./TagFiltersToolbar";

export type TagContextOptions = "product" | "ingredient" | "global" | "cms" | "supplier" | "organization" | "packaging";

export interface UseTagFiltersDataOptions {
    context?: TagContextOptions;
    /**
     * Controls whether to include inactive items in the results.
     * When true, both active and inactive items are included.
     * When false (default), only active items are included.
     */
    includeInactive?: boolean;
}

export const useTagFiltersData = (options: UseTagFiltersDataOptions) => {
    const { context, includeInactive = false } = options;

    // Fetch tag types
    const {
        tagTypes = [],
        isLoading: tagTypesLoading,
        error: tagTypesError,
    } = useGetAllTagTypes({
        context,
        active: !includeInactive,
        includeInactive,
    });

    // Fetch tag values for getting available values per tag type
    const {
        data: tagValuesData,
        isLoading: tagValuesLoading,
        error: tagValuesError,
    } = useGetAllTagValues({
        active: !includeInactive,
    });

    const tagValues = tagValuesData?.tagValues || [];

    // Transform tag types to filter format and include available values
    const filterTagTypes = useMemo((): TagType[] => {
        return tagTypes.map((tagType) => {
            // Find all values for this tag type
            const availableValues = tagValues
                .filter((value) => value.tagType?.id === tagType.id)
                .map((value) => value.name)
                .sort();

            return {
                id: tagType.id,
                slug: tagType.slug,
                name: tagType.name,
                color: tagType.color,
                availableValues,
            };
        });
    }, [tagTypes, tagValues]);

    return {
        tagTypes: filterTagTypes,
        tagValues: tagValues,
        isLoading: tagTypesLoading || tagValuesLoading,
        isReady: !tagTypesLoading && !tagValuesLoading,
        error: tagTypesError || tagValuesError,
    };
};
